// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { UNAUTHORIZED_ERROR } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLoggedInState } from '../../../context/LoggedInContext';
import { Box, Button, Typography } from '../../../lib/base';
import * as routes from '../../../routes';
import { LogoCircle } from '../image/LogoCircle';
import { PageLoading } from '../loading';

interface Props {
  error?: Error;
  retry?: () => void;
}
export default function ErrorView({ error, retry }: Props): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { setIsLoggedIn } = useLoggedInState();

  if (error?.message === `CLIENT:${UNAUTHORIZED_ERROR}`) {
    setIsLoggedIn(false);
    navigate(routes.makeSignInWithRedirect(location.pathname));

    return <PageLoading />;
  }

  let content: React.ReactElement;
  if (retry != null) {
    content = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography sx={{ textAlign: 'center' }} variant="subtitle1">
          Try going back to where you were, refreshing the page, or clicking reload below.
        </Typography>
        <Button sx={{ mt: 2 }} onClick={retry} fullWidth variant="contained" color="primary">
          <Typography variant="body1">Reload</Typography>
        </Button>
      </Box>
    );
  } else {
    let messageToUse = error?.message ?? 'Unknown error.';
    if (!messageToUse.endsWith('.') && !messageToUse.endsWith('!')) {
      messageToUse += '.';
    }
    if (messageToUse.startsWith('CLIENT:')) {
      messageToUse = messageToUse.substring('CLIENT:'.length);
    }
    content = (
      <Typography
        variant="subtitle1"
        sx={{ textAlign: 'center' }}
      >{`${messageToUse} Try going back to where you were or refreshing the page.`}</Typography>
    );
  }
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: {
          xs: 1,
          sm: 2,
        },
      }}
    >
      <LogoCircle />
      <Typography
        sx={{
          pb: 1,
          pt: 1,
        }}
        variant="h4"
      >
        Something went wrong!
      </Typography>
      {content}
    </Box>
  );
}
